import { DialogComponent } from '@theme/dialog';

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   price: string,
 *   image: string,
 *   alt: string
 * }} SelectedAddon
 */

/**
 * @extends {DialogComponent<{
 *   dialog: HTMLDialogElement,
 *   productsGrid: HTMLElement,
 *   selectedWrap: HTMLElement,
 *   selectedList: HTMLElement
 * }>}
 */
class ProductCharmsCta extends DialogComponent {
  requiredRefs = ['dialog', 'productsGrid', 'selectedWrap', 'selectedList'];

  /** @type {boolean} */
  #productsLoaded = false;

  /** @type {SelectedAddon[]} */
  #selectedAddons = [];

  /** @type {boolean} */
  #isLoading = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  showDialog() {
    super.showDialog();

    if (!this.#productsLoaded && !this.#isLoading) {
      this.#loadProducts();
    }
  }

  applySelection() {
    const selects = this.refs.productsGrid.querySelectorAll(
      '.addon-selection select.addon-variant-option, select.addon-variant-option'
    );

    const selectedMap = new Map();

    selects.forEach((selectEl) => {
      if (!(selectEl instanceof HTMLSelectElement)) return;

      const selectedOption = selectEl.options[selectEl.selectedIndex];
      if (!selectedOption) return;

      const id = (selectedOption.value || '').trim();
      if (!id) return;

      let image = selectedOption.getAttribute('data-variant-img') || selectedOption.getAttribute('data-image-src') || '';
      if (image.includes('no-image')) {
        image = selectedOption.getAttribute('data-image-src') || '';
      }

      selectedMap.set(id, {
        id,
        title: selectedOption.getAttribute('data-title') || selectedOption.textContent?.trim() || 'Charm',
        price: selectedOption.getAttribute('data-price') || '',
        image,
        alt: selectedOption.getAttribute('data-title') || 'Charm',
      });
    });

    this.#selectedAddons = Array.from(selectedMap.values());
    this.#renderSelectedAddons();
    this.#syncHiddenInputs();
    this.closeDialog();
  }

  async #loadProducts() {
    const sourceUrl = this.dataset.sourceUrl || '/collections/charms?view=ajax_addons_block';
    this.#isLoading = true;
    this.refs.productsGrid.innerHTML = '<p class="product-charms-panel__error">Loading charms...</p>';

    try {
      const response = await fetch(sourceUrl, { credentials: 'same-origin' });
      const html = await response.text();
      this.refs.productsGrid.innerHTML = html;

      this.#bindVariantImageSwaps();
      this.#productsLoaded = true;
    } catch (error) {
      this.refs.productsGrid.innerHTML =
        '<p class="product-charms-panel__error">Unable to load charms right now. Please try again.</p>';
      console.error(error);
    } finally {
      this.#isLoading = false;
    }
  }

  #bindVariantImageSwaps() {
    const cards = this.refs.productsGrid.querySelectorAll('.card');

    cards.forEach((card) => {
      const selectEl = card.querySelector('.addon-variant-option');
      const mediaEl = card.querySelector('.card-media');
      if (!(selectEl instanceof HTMLSelectElement) || !(mediaEl instanceof HTMLElement)) return;

      selectEl.addEventListener('change', () => {
        const selectedOption = selectEl.options[selectEl.selectedIndex];
        if (!selectedOption) return;

        let image = selectedOption.getAttribute('data-variant-img') || selectedOption.getAttribute('data-image-src') || '';
        if (image.includes('no-image')) {
          image = selectedOption.getAttribute('data-image-src') || '';
        }
        if (!image) return;

        const imageEl = document.createElement('img');
        imageEl.className = 'motion-reduce';
        imageEl.src = image;
        imageEl.alt = selectedOption.getAttribute('data-title') || '';

        mediaEl.innerHTML = '';
        mediaEl.appendChild(imageEl);
      });
    });
  }

  #handleClick = (event) => {
    const removeButton = event.target instanceof Element ? event.target.closest('[data-remove-addon-id]') : null;

    if (removeButton) {
      event.preventDefault();
      const id = removeButton.getAttribute('data-remove-addon-id') || '';
      if (!id) return;

      this.#selectedAddons = this.#selectedAddons.filter((addon) => addon.id !== id);
      this.#renderSelectedAddons();
      this.#syncHiddenInputs();
    }
  };

  #renderSelectedAddons() {
    this.refs.selectedList.textContent = '';

    if (!this.#selectedAddons.length) {
      this.refs.selectedWrap.hidden = true;
      return;
    }

    const fragment = document.createDocumentFragment();

    this.#selectedAddons.forEach((addon) => {
      const row = document.createElement('div');
      row.className = 'product-charms-cta__selected-item';

      const img = document.createElement(addon.image ? 'img' : 'div');
      img.className = 'product-charms-cta__selected-thumb';
      if (img instanceof HTMLImageElement) {
        img.src = addon.image;
        img.alt = addon.alt;
        img.loading = 'lazy';
      }

      const textWrap = document.createElement('div');

      const title = document.createElement('p');
      title.className = 'product-charms-cta__selected-title';
      title.textContent = addon.title;

      const price = document.createElement('p');
      price.className = 'product-charms-cta__selected-price';
      price.textContent = addon.price;

      textWrap.appendChild(title);
      if (addon.price) textWrap.appendChild(price);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'product-charms-cta__selected-remove';
      remove.setAttribute('data-remove-addon-id', addon.id);
      remove.setAttribute('aria-label', `Remove ${addon.title}`);
      remove.textContent = '×';

      row.appendChild(img);
      row.appendChild(textWrap);
      row.appendChild(remove);
      fragment.appendChild(row);
    });

    this.refs.selectedList.appendChild(fragment);
    this.refs.selectedWrap.hidden = false;
  }

  #syncHiddenInputs() {
    const sectionRoot = this.closest('.shopify-section') || this.closest('product-card') || document;
    const addToCartForm = sectionRoot.querySelector('[data-type="add-to-cart-form"]');
    if (!(addToCartForm instanceof HTMLFormElement)) return;

    addToCartForm.querySelectorAll('input[data-charms-addon-input="true"]').forEach((input) => input.remove());

    if (!this.#selectedAddons.length) return;

    const existingIndices = Array.from(addToCartForm.querySelectorAll('input[name^="items["][name$="][id]"]'))
      .map((input) => {
        const match = input.getAttribute('name')?.match(/^items\[(\d+)\]\[id\]$/);
        return match ? Number(match[1]) : null;
      })
      .filter((index) => index !== null);

    let nextIndex = existingIndices.length ? Math.max(...existingIndices) + 1 : 1;

    this.#selectedAddons.forEach((addon) => {
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = `items[${nextIndex}][id]`;
      idInput.value = addon.id;
      idInput.setAttribute('data-charms-addon-input', 'true');
      idInput.setAttribute('data-charms-addon-id', addon.id);

      const quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = `items[${nextIndex}][quantity]`;
      quantityInput.value = '1';
      quantityInput.setAttribute('data-charms-addon-input', 'true');
      quantityInput.setAttribute('data-charms-addon-id', addon.id);

      addToCartForm.appendChild(idInput);
      addToCartForm.appendChild(quantityInput);
      nextIndex += 1;
    });
  }
}

if (!customElements.get('product-charms-cta')) {
  customElements.define('product-charms-cta', ProductCharmsCta);
}
